import { AuthProvider_UserFragment } from '@/graphql';
import { logger } from '@/utils/logger';

interface CreateUserParams {
  oryId: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
}

interface InsertOryIdIntoUserParams {
  userId: string;
  oryId: string;
}

/**
 * Creates a new user with associated personal entity
 * @param params User creation parameters
 * @returns Success state and user ID if successful
 */
const createUser = async ({
  oryId,
  phoneNumber,
  firstName,
  lastName,
}: CreateUserParams): Promise<{
  success: boolean;
  user?: AuthProvider_UserFragment;
}> => {
  logger.debug('Creating new user:', { firstName, lastName, phoneNumber });

  if (!oryId) {
    logger.error('Missing Ory ID when creating user');
    return { success: false };
  }

  try {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    if (!backendUrl) {
      throw new Error(
        'Backend URL is not configured. Please set NEXT_PUBLIC_BACKEND_URL.',
      );
    }

    // Call the backend API to register the user
    const response = await fetch(`${backendUrl}/api/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        oryId,
        phoneNumber,
        firstName,
        lastName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'User registration failed');
    }

    const responseJson = await response.json();

    const newUser = responseJson?.data.user;

    if (!newUser) {
      throw new Error('Failed to fetch created user');
    }

    return { success: true, user: newUser };
  } catch (error: unknown) {
    logger.error(
      `Registration error: ${JSON.stringify(
        {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
        undefined,
        2,
      )}`,
    );
    return { success: false };
  }
};

const insertOryIdIntoUser = async ({
  userId,
  oryId,
}: InsertOryIdIntoUserParams): Promise<{
  success: boolean;
  user?: AuthProvider_UserFragment;
}> => {
  try {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    if (!backendUrl) {
      throw new Error(
        'Backend URL is not configured. Please set NEXT_PUBLIC_BACKEND_URL.',
      );
    }

    const response = await fetch(`${backendUrl}/api/insertOryIdIntoUser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        oryId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to insert Ory ID into user');
    }

    const responseJson = await response.json();
    const returningUser = responseJson?.user;

    return { success: true, user: returningUser };
  } catch (error: unknown) {
    logger.error(
      `Insert Ory ID into User error: ${JSON.stringify(
        {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        },
        undefined,
        2,
      )}`,
    );
    return { success: false };
  }
};

export { createUser, insertOryIdIntoUser };
